import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { withTheme } from 'styled-components';
import { observeStore, getAppconfig } from '4ff-lib-foundation/lib/AppFrame/publicAPI/store';
import PendingIndicator from '4ff-lib-foundation/lib/components/PendingIndicator';
import isEqual from 'lodash.isequal';
import { LAYOUT_LEVELS } from '../../constants';
import history from '../../services/history';
import queryString from '../../services/queryString';
import ExternalBreadcrumbs from '../../components/ExternalBreadcrumbs';
import Layout from '../../components/Layout';
import LayoutL2 from '../../components/LayoutL2';
import { getSolutionById, getModuleById, findPath } from '../../utils';
import { RootContainer, FallbackContainer, FallbackMsg } from './styles';

class Root extends PureComponent {
  static propTypes = {
    loading: PropTypes.bool.isRequired,
    tree: PropTypes.array,
    getSitemap: PropTypes.func.isRequired,
    location: PropTypes.shape({
      search: PropTypes.string.isRequired,
      pathname: PropTypes.string.isRequired,
    }).isRequired,
    t: PropTypes.func.isRequired,
    theme: PropTypes.object.isRequired,
  };

  static defaultProps = {
    tree: [],
  };

  constructor() {
    super();
    this.appconfig = getAppconfig();

    if (this.appconfig.defaultRoute && !window.location.search) {
      window.location.href = this.appconfig.defaultRoute;
    }
  }

  componentDidMount() {
    const selector = (state) => state.menu.solutions;
    this.unsubscribeStore = observeStore(selector, (storeTree) => {
      if (!isEqual(storeTree, this.props.tree)) {
        this.props.getSitemap();
      }
    });

    setTimeout(() => {
      this.props.getSitemap();
    }, 300);
  }

  componentWillUnmount() {
    this.unsubscribeStore();
  }

  onSectionChanged = (id) => {
    const query = queryString.parse(this.props.location.search);
    const newQuery = id
      ? { solution: query.solution, module: query.module, section: id }
      : { solution: query.solution, module: query.module };
    history.push({
      pathname: this.props.location.pathname,
      search: queryString.stringify(newQuery),
    });
  }

  renderBlank = () => (
    <FallbackContainer>
      <FallbackMsg data-test-role="no-ids-label">
        {this.props.t('LPBD.noIdsProvided')}
      </FallbackMsg>
    </FallbackContainer>
  );

  renderDashboardContent = () => {
    if (!this.props.tree) return this.renderBlank();
    const query = queryString.parse(this.props.location.search);

    if (!query.solution) {
      // find default or the first one
      const defaultL1 = this.props.tree.find(l1 => l1.meta && l1.meta.default);
      const firstL1 = this.props.tree.find(() => true);
      query.solution = (defaultL1 && defaultL1.id) || (firstL1 && firstL1.id) || null;
      // add solution id to url params
      if (query.solution) {
        history.replace({
          pathname: this.props.location.pathname,
          search: queryString.stringify(query),
        });
      }
    }

    if (query.solution) {
      const solution = getSolutionById(this.props.tree, query.solution);

      // show empty screen when there is no tree yet
      if (!solution) return this.renderBlank();

      // if solution has only a module as children then redirect to a module
      if (!query.module
        && solution.children.length === 1
        && solution.children[0].children
      ) {
        query.module = solution.children[0].id;
        // add module id to url params
        history.replace({
          pathname: this.props.location.pathname,
          search: queryString.stringify(query),
        });
      }

      if (query.module) {
        const module = getModuleById(solution, query.module);

        if (!module) {
          return this.renderBlank();
        }

        if (query.item) {
          const targetPath = findPath(solution, query.item, 'id');
          targetPath.reverse(); // mutates array
          const targetNode = targetPath[targetPath.length - 1];

          if (!targetNode) {
            return this.renderBlank();
          }

          return (
            <ExternalBreadcrumbs themeName={this.props.theme.name}>
              <Layout node={targetNode} level={LAYOUT_LEVELS.item} breadcrumbs={targetPath} />
            </ExternalBreadcrumbs>
          );
        }

        return (
          <ExternalBreadcrumbs themeName={this.props.theme.name}>
            <LayoutL2
              solution={solution}
              moduleID={query.module}
              sectionID={query.section}
              onSectionChanged={this.onSectionChanged}
              menuL3AsSection={this.appconfig.menuL3AsSection}
            />
          </ExternalBreadcrumbs>
        );
      }

      return <Layout node={solution} level={LAYOUT_LEVELS.module} breadcrumbs={[solution]} />;
    }

    return this.renderBlank();
  };

  render() {
    return (
      <RootContainer>
        {this.props.loading || (this.appconfig.defaultRoute && !this.props.location.search)
          ? <PendingIndicator />
          : this.renderDashboardContent()}
      </RootContainer>
    );
  }
}

const TranslatedRoot = withTranslation()(Root);
export default withTheme(TranslatedRoot);
